/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable consistent-return */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import ShimmerContent from './ShimmerContent';
import {
  getAssignedWorkoutItemsForCalendarByDate,
  getAssignedWorkoutItemsForAthleteByDate,
  getRecurringWorkoutsByDate,
  fetchNonDateBasedProgramItemsByDayRange,
  setSelectedReduxCalendar,
  setSelectedReduxAthlete,
  clearBeforeRecurringCalendarItems,
  clearAfterRecurringCalendarItems,
  setSelectedDate,
  clearCalendarItems,
  setSelectedProgramDayNumber,
  setIsPrimaryAnimatedSideBarOpen,
} from '../../ducks/calendarActions';

// Helper: Check if approaching edge of cached data
const isApproachingEdge = (selectedDate, calendarItemsDateRange, viewType, bufferDays = 7) => {
  // No range cached yet, need to fetch
  if (!calendarItemsDateRange.earliest || !calendarItemsDateRange.latest) {
    return true;
  }

  const current = moment(selectedDate);
  const earliest = moment(calendarItemsDateRange.earliest);
  const latest = moment(calendarItemsDateRange.latest);

  // Check if we're within buffer of edges
  const daysTillStart = current.diff(earliest, 'days');
  const daysTillEnd = latest.diff(current, 'days');

  // For week view, check the entire week range
  if (viewType === 'weekView') {
    const weekStart = current.clone().startOf('week');
    const weekEnd = current.clone().endOf('week');
    return weekStart.diff(earliest, 'days') < bufferDays
      || latest.diff(weekEnd, 'days') < bufferDays;
  }

  return daysTillStart < bufferDays || daysTillEnd < bufferDays;
};

// Helper: Calculate 69-day padded date range
const calculate69DayPaddedRange = (selectedDate) => {
  const reduxDate = moment(selectedDate);

  // Calculate current month + next month days
  const currentMonthStart = reduxDate.clone().startOf('month');
  const nextMonthEnd = reduxDate.clone().add(1, 'month').endOf('month');
  const currentAndNextMonthDays = nextMonthEnd.diff(currentMonthStart, 'days') + 1;

  // Calculate max previous days such that total <= 69
  const maxPreviousDays = Math.max(0, 69 - currentAndNextMonthDays);

  // Calculate actual start date
  const fetchStartDate = currentMonthStart.clone().subtract(maxPreviousDays, 'days');

  return {
    firstDayMonth: fetchStartDate.format('YYYY-MM-DD'),
    lastDayMonth: nextMonthEnd.format('YYYY-MM-DD'),
  };
};

const Container = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: visible;
`;

const ExerciseContainer = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
`;

const shimmerCount = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const CalendarPanel = ({ scrollContainerRef }) => {
  const dispatch = useDispatch();
  const onBeforeCapture = useSelector((state) => state.calendar.data.onBeforeCapture);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const isCalendarItemsLoading = useSelector((state) => state.calendar.ui.isCalendarItemsLoading);
  const isSavedItemsLoading = useSelector((state) => state.calendar.ui.isSavedItemsLoading);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );

  /** Data fetching related selectors */
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarItemsDateRange = useSelector((state) => state.calendar.data.calendarItemsDateRange);
  const recurringItemsDateRange = useSelector(
    (state) => state.calendar.data.recurringItemsDateRange,
  );

  useEffect(() => {
    const hasProgram = Object.keys(selectedReduxNonDateBasedProgram || {}).length > 0;
    if (programType === 'free-form-program' && hasProgram) {
      dispatch(setSelectedReduxAthlete({}));
      dispatch(setSelectedReduxCalendar({}));
      dispatch(clearCalendarItems());
      dispatch(clearBeforeRecurringCalendarItems());
      dispatch(clearAfterRecurringCalendarItems());
      dispatch(setSelectedDate(new Date()));
    }
  }, [
    programType,
    Object.keys(selectedReduxNonDateBasedProgram).length,
    Object.keys(selectedReduxAthlete).length,
    Object.keys(selectedReduxCalendar).length,
  ]);

  useEffect(() => {
    /** need to close side bar if change from calendar to non date based
     * and visa versa, this prevents the user from getting access to a side bar
     * they should not have access to, for expample non date based
     * should not have access to calendar but if the side bar is open
     * when the user changes program types they would have access to the calendar
     * closing the side bar prevent this issue
     */
    dispatch(setIsPrimaryAnimatedSideBarOpen(false));
  }, [
    programType,
  ]);

  /** Data fetching logic moved from CalendarLayout */
  useEffect(() => {
    // Check if we need to fetch based on smart caching logic
    const shouldFetch = isApproachingEdge(selectedDate, calendarItemsDateRange, viewType, 7);

    if (!shouldFetch) {
      // Data exists and we're not near the edge, skip fetch
      return;
    }

    // Use helper to calculate 69-day padded date range
    const { firstDayMonth, lastDayMonth } = calculate69DayPaddedRange(selectedDate);

    /** Fetch main calendar items */
    if (Object.keys(selectedReduxCalendar).length && Object.keys(currentUser).length) {
      if (selectedReduxCalendar?.id) {
        dispatch(getAssignedWorkoutItemsForCalendarByDate(
          currentUser,
          firstDayMonth,
          lastDayMonth,
          selectedReduxCalendar.id,
        ));
      }
    } else if (Object.keys(selectedReduxAthlete).length && Object.keys(currentUser).length) {
      dispatch(getAssignedWorkoutItemsForAthleteByDate(
        currentUser,
        firstDayMonth,
        lastDayMonth,
        selectedReduxAthlete.id,
      ));
    }
  }, [
    selectedReduxCalendar,
    selectedReduxAthlete,
    currentUser,
    selectedDate,
  ]);

  /** Seperate useEffect to fetch data for non date based programs when in progam library view */
  useEffect(() => {
    const startDay = null;
    const endDay = null;
    /** Fetch non date based items */
    if (Object.keys(selectedReduxNonDateBasedProgram).length && Object.keys(currentUser).length) {
      if (selectedReduxNonDateBasedProgram?.id) {
        dispatch(fetchNonDateBasedProgramItemsByDayRange(
          currentUser.accountCode,
          selectedReduxNonDateBasedProgram.id,
          startDay,
          endDay,
        ));
      }
    }
  }, [
    selectedReduxNonDateBasedProgram,
    currentUser,
    selectedProgramDayNumber,
  ]);

  useEffect(() => {
    /** when we change the selected program we always need to reset
     * the selectedProgramDayNumber to 0
     */
    dispatch(setSelectedProgramDayNumber(0));
  }, [
    selectedReduxNonDateBasedProgram,
  ]);

  /** Separate effect for recurring workouts that only runs when needed
  and only in calendar based view */
  const shouldFetchRecurring = useSelector(
    (state) => state.calendar.data.shouldFetchRecurring,
  );
  useEffect(() => {
    if (!Object.keys(currentUser).length || programType === 'free-form-program') {
      return;
    }

    // Check if we need to fetch based on smart caching logic
    const shouldFetch = isApproachingEdge(
      selectedDate,
      recurringItemsDateRange,
      viewType,
      7,
    );

    if (!shouldFetch) {
      // Data exists and we're not near the edge, skip fetch
      return;
    }

    // Use helper to calculate 69-day padded date range
    const { firstDayMonth, lastDayMonth } = calculate69DayPaddedRange(selectedDate);

    if (Object.keys(selectedReduxCalendar).length && selectedReduxCalendar?.id) {
      dispatch(getRecurringWorkoutsByDate(
        currentUser,
        firstDayMonth,
        lastDayMonth,
        selectedReduxCalendar.id,
      ));
    } else if (Object.keys(selectedReduxAthlete).length && selectedReduxAthlete?.id) {
      dispatch(getRecurringWorkoutsByDate(
        currentUser,
        firstDayMonth,
        lastDayMonth,
        selectedReduxAthlete.id,
        true,
      ));
    }
  }, [
    selectedReduxCalendar,
    selectedReduxAthlete,
    currentUser,
    selectedDate,
    shouldFetchRecurring,
  ]);

  return (
    <Container setHeightTo100={onBeforeCapture}>
      <ExerciseContainer
        setHeightTo100={onBeforeCapture}
        viewType={viewType}
      >
        {viewType === 'dayView' ? (
          (programType !== 'free-form-program' && !isCalendarItemsLoading) || (programType === 'free-form-program' && !isSavedItemsLoading) ? (
            <DayView scrollContainerRef={scrollContainerRef} />
          )
            : shimmerCount.map((_, i) => <ShimmerContent key={i} />)
        ) : viewType === 'weekView' ? (
          <WeekView
            isWeekDayItemsLoading={(programType !== 'free-form-program' && isCalendarItemsLoading) || (programType === 'free-form-program' && (isSavedItemsLoading))}
          />
        ) : (
          <MonthView
            isMonthItemsLoading={(programType !== 'free-form-program' && isCalendarItemsLoading) || (programType === 'free-form-program' && (isSavedItemsLoading))}
          />
        )}
      </ExerciseContainer>
    </Container>
  );
};

CalendarPanel.propTypes = { };
export default CalendarPanel;
